import { AlertTriangle, Loader2 } from "lucide-react";
import React, { useEffect, useState } from "react";
import { toast } from "sonner";

import { createConnection } from "@/api/connections";
import { CONFIG_TYPE_TITLES } from "@/api/onec_bases";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { extractErrorMessage } from "@/lib/api-errors";
import { useOneCBases } from "@/lib/use-onec-bases";

/** Новое подключение в SSO-сессии: база из справочника, без логина и пароля.
 *  Конфигурация и адрес берутся из справочника; в 1С портал входит токеном SSO
 *  от имени пользователя 1С, заданного в учётной записи SSO (`metadata_key` базы). */
export function SsoConnectionDialog({
  open,
  onClose,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}): React.ReactElement {
  const { data, isLoading, error, refetch } = useOneCBases();
  const [name, setName] = useState("");
  const [baseId, setBaseId] = useState("");
  const [busy, setBusy] = useState(false);
  // Отказ сервера — строкой в диалоге, не только тостом.
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName("");
      setBaseId("");
      setFormError(null);
    }
  }, [open]);

  const picked = data?.bases.find((b) => b.id === baseId);
  // Для выбранной базы нет пользователя 1С в учётке SSO — подключение создать
  // можно, но 1С ответит отказом: говорим заранее.
  const noOneCUser =
    picked !== undefined && data !== undefined && !data.onec_metadata_keys.includes(picked.metadata_key);

  const onPickBase = (id: string) => {
    setBaseId(id);
    // Название — имя базы, если человек его ещё не вводил.
    const base = data?.bases.find((b) => b.id === id);
    if (base && !name.trim()) setName(base.name);
  };

  const handleSubmit = async () => {
    const missing = [!name.trim() && "Название", !baseId && "База 1С"].filter(Boolean);
    if (missing.length > 0) {
      setFormError(`Заполните обязательные поля: ${missing.join(", ")}`);
      return;
    }
    setFormError(null);
    setBusy(true);
    try {
      await createConnection({ name: name.trim(), onec_base_id: baseId });
      toast.success("Подключение создано");
      onSaved();
    } catch (err) {
      const msg = extractErrorMessage(err, "Ошибка создания");
      setFormError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Новое подключение к 1С</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Вы вошли через SSO: логин и пароль 1С не нужны — выберите базу из справочника.
          </p>
          {isLoading && <p className="text-sm text-muted-foreground">Загрузка справочника баз…</p>}
          {error && (
            <div className="flex items-center gap-3 text-sm text-destructive">
              Справочник баз 1С не загрузился: {extractErrorMessage(error, "нет ответа")}
              <Button size="sm" variant="outline" onClick={() => void refetch()}>
                Повторить
              </Button>
            </div>
          )}
          {data && data.bases.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Справочник баз 1С пуст — обратитесь к администратору портала.
            </p>
          )}
          {data && data.bases.length > 0 && (
            <>
              <div className="space-y-2">
                <Label htmlFor="sso-conn-base">База 1С *</Label>
                <select
                  id="sso-conn-base"
                  value={baseId}
                  onChange={(e) => onPickBase(e.target.value)}
                  className="flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm"
                >
                  <option value="">— выберите базу —</option>
                  {data.bases.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name} · {CONFIG_TYPE_TITLES[b.config_type] ?? b.config_type} ({b.base_url})
                    </option>
                  ))}
                </select>
              </div>
              {noOneCUser && picked && (
                <div
                  role="alert"
                  className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
                >
                  <AlertTriangle className="mt-0.5 size-4 shrink-0" />
                  <div>
                    <div className="font-semibold">Не настроен доступ к 1С</div>
                    <div>
                      В учётной записи SSO не задан пользователь 1С для этой базы (
                      {picked.metadata_key}) — обратитесь к администратору.
                    </div>
                  </div>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="sso-conn-name">Название *</Label>
                <Input
                  id="sso-conn-name"
                  placeholder="БП ИП Еграшкин"
                  value={name}
                  onChange={(e) => setName(e.currentTarget.value)}
                />
              </div>
            </>
          )}
          {formError && (
            <p className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {formError}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={busy || !data || data.bases.length === 0}>
            {busy && <Loader2 className="size-4 animate-spin" />}
            Создать
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
